import React from 'react';
import { Popover, Box, Typography, Chip, InputBase, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../themes/typography';

// Where the user last pressed, so the prompt opens next to the reject button that was clicked
// without every caller having to pass an anchor.
let lastPointer = null;
if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', (e) => { lastPointer = { top: e.clientY, left: e.clientX }; }, true);
}

/**
 * Small popover at the reject click: one click on a common reason rejects, or type another one and
 * press Enter. Nothing is rejected without a reason; Esc or clicking away cancels.
 */
const RejectReasonDialog = ({ open, onClose, onConfirm, loading = false, experimentCount = 1 }) => {
  const { t } = useTranslation();
  const [customReason, setCustomReason] = React.useState('');
  const [position, setPosition] = React.useState(null);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (open) {
      setPosition(lastPointer || { top: window.innerHeight / 2, left: window.innerWidth / 2 });
      setCustomReason('');
    }
  }, [open]);

  const reasons = [
    t('noFit'), t('tooTall'), t('spiralSize'), t('formatMismatch'),
    t('priceAgreed'), t('lowMargin'), t('wontSell'), t('outOfStock'),
  ];

  const confirm = (reason) => {
    if (!reason.trim() || loading) return;
    onConfirm(reason.trim());
    onClose();
  };

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={position || undefined}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      onClick={(e) => e.stopPropagation()}
      // The popover's focus trap grabs focus while it opens; once open, hand it to the text field so
      // a typed reason lands there whole.
      TransitionProps={{ onEntered: () => inputRef.current?.focus() }}
      slotProps={{ paper: { sx: { width: 260, p: 1.25, borderRadius: '8px', mt: 0.5 } } }}
    >
      <Typography sx={{ fontSize: '12px', fontWeight: 600, color: colors.text.primary, mb: 1 }}>
        {t('rejectWhy')}{experimentCount > 1 ? ` (${experimentCount})` : ''}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
        {reasons.map((reason) => (
          <Chip
            key={reason}
            label={reason}
            size="small"
            variant="outlined"
            disabled={loading}
            onClick={() => confirm(reason)}
            sx={{ fontSize: '12px', '&:hover': { borderColor: '#ef5350', color: '#d32f2f' } }}
          />
        ))}
      </Box>
      <Box
        component="form"
        onSubmit={(e) => { e.preventDefault(); confirm(customReason); }}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: `1px solid ${colors.border.light}`, borderRadius: '6px', pl: 1 }}
      >
        <InputBase
          inputRef={inputRef}
          value={customReason}
          onChange={(e) => setCustomReason(e.target.value)}
          placeholder={t('rejectOtherReason')}
          inputProps={{ 'aria-label': t('rejectOtherReason') }}
          sx={{ flex: 1, fontSize: '12px' }}
        />
        <Button
          type="submit"
          size="small"
          disabled={!customReason.trim() || loading}
          sx={{ textTransform: 'none', fontSize: '12px', color: '#d32f2f', minWidth: 0 }}
        >
          {t('reject')}
        </Button>
      </Box>
    </Popover>
  );
};

export default RejectReasonDialog;
